import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Select, Tooltip, Button } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { useLazyGetOrganizationAndChildsQuery } from "../../../redux/organizations/organizationAPI";
import { updateFilters } from "../../../redux/reports/reportsSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// FLATBUFFERS
import formatUuidT from "../../../flatbuffers/display/uuid";
// INTERFACES
import { RootState } from "../../../app/store";
import { OrganizationInterface } from "../../../interfaces/Organizations.interface";
import { ReportSliceFiltersInterface } from "../../../interfaces/Reports.interface";

function ReportsFilterGetOrganizations() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Option } = Select;
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [organizations, setOrganizations] = useState<OrganizationInterface[]>([]);
  const [launchFirstSearch, setLaunchFirstSearch] = useState<boolean>(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { filters } = useSelector((state: RootState) => state.report);
  const { userLogged, globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetOrganizations, { data, isLoading, isError, isFetching }] =
    useLazyGetOrganizationAndChildsQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getOrganization = async (orgId: string) => {
    const token = await GETJwtToken();
    triggerGetOrganizations({
      page: 1,
      limit: 9999,
      token,
      orgId,
    });
  };

  const initialData = () => {
    if (userLogged && userLogged.org && userLogged.org.id) {
      getOrganization(formatUuidT(userLogged.org.id));
    }
  };

  const onChangeOrganization = (newValue: string) => {
    const index = organizations.findIndex(
      (item: OrganizationInterface) => formatUuidT(item.id) === newValue,
    );
    const copy: ReportSliceFiltersInterface = structuredClone(filters);
    // Save selected organization
    copy.organizationName = String(organizations[index].name ?? "");
    copy.organizationId = formatUuidT(organizations[index].id);
    // Validate first default data
    if (filters.getFirstDefaultValue) {
      copy.getFirstDefaultValue = false;
    }
    dispatch(updateFilters(copy));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (globalOrganizationId) {
      getOrganization(globalOrganizationId);
    } else {
      initialData();
    }
  }, []);

  // Sync with the global org selection from the header dropdown
  useEffect(() => {
    if (globalOrganizationId && globalOrganizationId !== filters.organizationId) {
      getOrganization(globalOrganizationId);
    }
  }, [globalOrganizationId]);

  useEffect(() => {
    const newData = structuredClone(data);
    if (newData && newData.data) {
      if (data.data.children) {
        setOrganizations([
          {
            id: data.data.id,
            address: data.data.address,
            city: data.data.city,
            country: data.data.country,
            name: data.data.name,
            phoneCode: data.data.phone_code ?? data.data.phoneCode,
            phone: data.data.phone,
            state: data.data.state,
            type: data.data.type,
            parentId: null,
          },
          ...data.data.children,
        ]);
      } else {
        setOrganizations([
          {
            id: data.data.id,
            address: data.data.address,
            city: data.data.city,
            country: data.data.country,
            name: data.data.name,
            phoneCode: data.data.phone_code ?? data.data.phoneCode,
            phone: data.data.phone,
            state: data.data.state,
            type: data.data.type,
            parentId: data.data.parent_id ?? data.data.parentId ?? null,
          },
        ]);
      }
    }
  }, [data]);

  useEffect(() => {
    setOrganizations([]);
  }, [isError]);

  useEffect(() => {
    if (!organizations || organizations.length === 0) return;
    // When globalOrganizationId is set from the header, select it
    if (globalOrganizationId) {
      const match = organizations.find(
        (org: OrganizationInterface) => formatUuidT(org.id) === globalOrganizationId,
      );
      if (match && formatUuidT(match.id) !== filters.organizationId) {
        onChangeOrganization(formatUuidT(match.id));
        setLaunchFirstSearch(true);
        return;
      }
    }
    // Fallback: first-load default selection
    if (filters.getFirstDefaultValue && userLogged) {
      const match = organizations.find(
        (org: OrganizationInterface) =>
          formatUuidT(org.id) === (globalOrganizationId || userLogged.organizationId),
      );
      if (match) {
        onChangeOrganization(formatUuidT(match.id));
        setLaunchFirstSearch(true);
      }
    }
  }, [organizations]);

  useEffect(() => {
    if (launchFirstSearch) {
      setLaunchFirstSearch(false);
    }
  }, [launchFirstSearch]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="generalStyles__width100" style={{ display: "flex", alignItems: "center" }}>
      <Button size="small" disabled style={{ marginRight: 4, minWidth: "fit-content" }}>
        {t("report.selectOrganization")}
      </Button>
      <Tooltip title={organizations.length === 0 ? t("report.noOrganizations") : ""}>
        <Select
          className="generalStyles__width100"
          disabled={organizations.length === 0}
          showSearch
          size="small"
          optionFilterProp="children"
          placeholder={t("report.selectOrganization")}
          loading={isLoading || isFetching}
          value={filters.organizationId === "" ? null : filters.organizationId}
          onChange={onChangeOrganization}
        >
          {organizations.map((organization: OrganizationInterface) => (
            <Option key={formatUuidT(organization.id)} value={formatUuidT(organization.id)}>
              {organization.name}
            </Option>
          ))}
        </Select>
      </Tooltip>
    </div>
  );
}

export default ReportsFilterGetOrganizations;
